/**
 * Settings → "Backup & Restore" (F5). Lets the operator download a single
 * JSON file containing the entire ReefApp state (time-series DB + all
 * `./data/*.json` + selected runtime config) and restore it onto a clean
 * Pi. Credentials (`secrets.json`) are EXCLUDED unless the operator ticks
 * "include credentials". Restore is destructive and is gated behind a
 * confirm modal; on success the agent must be restarted to reopen its DB.
 */
import { useRef, useState } from 'react';

import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { backupApi } from '../../lib/backup-api';

type Status =
  | { kind: 'idle' }
  | { kind: 'working'; message: string }
  | { kind: 'ok'; message: string }
  | { kind: 'error'; message: string };

function filenameStamp(iso: string): string {
  // ISO has ':' which is illegal in filenames on Windows → replace.
  return iso.replace(/[:.]/g, '-');
}

export function BackupRestoreSection() {
  const [includeSecrets, setIncludeSecrets] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleDownload() {
    setStatus({ kind: 'working', message: 'Building backup…' });
    const res = await backupApi.download(includeSecrets);
    if (!res.ok) {
      setStatus({ kind: 'error', message: `Backup failed: ${res.error}` });
      return;
    }
    const json = JSON.stringify(res.value, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reefapp-backup-${filenameStamp(res.value.createdAt)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatus({
      kind: 'ok',
      message: res.value.includesSecrets
        ? 'Backup downloaded (includes credentials).'
        : 'Backup downloaded.',
    });
  }

  function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setPendingFile(file);
    if (file) setStatus({ kind: 'idle' });
  }

  async function handleRestoreConfirmed() {
    const file = pendingFile;
    if (!file) return;
    setStatus({ kind: 'working', message: 'Restoring…' });
    let parsed: unknown;
    try {
      const text = await file.text();
      parsed = JSON.parse(text) as unknown;
    } catch (err) {
      setStatus({
        kind: 'error',
        message: `Could not read file: ${
          err instanceof Error ? err.message : String(err)
        }`,
      });
      setPendingFile(null);
      return;
    }
    const res = await backupApi.restore(parsed);
    if (!res.ok) {
      setStatus({ kind: 'error', message: `Restore failed: ${res.error}` });
      setPendingFile(null);
      return;
    }
    setStatus({
      kind: 'ok',
      message: res.value.restartRequired
        ? 'Restore complete. Restart the controller to finish (the database was replaced).'
        : 'Restore complete.',
    });
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function cancelRestore() {
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const working = status.kind === 'working';

  return (
    <Card className="mt-6" data-testid="settings-backup-section">
      <CardHeader>
        <CardTitle>Backup &amp; Restore</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-xs text-reef-300 mb-4">
          Download a single file containing your entire setup — sensor
          history, schedules, calendar, test results, and configuration. Keep
          it somewhere safe so you can restore your tank onto a fresh
          controller.
        </p>

        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-reef-100">
            <input
              type="checkbox"
              checked={includeSecrets}
              onChange={(e) => setIncludeSecrets(e.target.checked)}
              data-testid="backup-include-secrets"
            />
            Include stored credentials in the backup
          </label>

          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleDownload()}
            disabled={working}
            data-testid="backup-download"
          >
            Download backup
          </Button>
        </div>

        <hr className="my-5 border-reef-700/50" />

        <p className="text-xs text-reef-300 mb-3">
          Restore from a backup file. This <strong>replaces</strong> all
          current data on the controller and cannot be undone.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={handleFilePicked}
          disabled={working}
          data-testid="backup-restore-file"
          className="block text-xs text-reef-200 file:mr-3 file:rounded file:border-0 file:bg-reef-700 file:px-3 file:py-1.5 file:text-reef-100"
        />

        {status.kind !== 'idle' ? (
          <p
            className={`mt-4 text-sm ${
              status.kind === 'error'
                ? 'text-red-400'
                : status.kind === 'ok'
                  ? 'text-green-400'
                  : 'text-reef-300'
            }`}
            data-testid="backup-status"
            role="status"
          >
            {status.message}
          </p>
        ) : null}
      </CardBody>

      {pendingFile ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          data-testid="backup-restore-dialog"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Restore from backup?</CardTitle>
            </CardHeader>
            <CardBody>
              <p className="text-sm text-reef-200">
                This will overwrite ALL current data on the controller with
                the contents of{' '}
                <span className="text-reef-100">{pendingFile.name}</span>. Your
                current history, schedules, and settings will be lost. This
                cannot be undone.
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelRestore}
                  data-testid="backup-restore-cancel"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleRestoreConfirmed()}
                  data-testid="backup-restore-confirm"
                >
                  Overwrite &amp; restore
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </Card>
  );
}
